import React from "react";
import DataTable from "components/common/table/DataTable";
import { useTranslation } from "react-i18next";
import {
  useDisclosure,
  Input,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Switch,
  Selection,
} from "@heroui/react";
import { NavLink } from "react-router-dom";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import AddCustomerModal from "components/common/modal/AddCustomerModal";
import SecondaryActions from "./CustomerSettingsSecondaryActions";

import { LuChevronDown } from "react-icons/lu";
import { IoSearchSharp } from "react-icons/io5";
import { MdOutlineAutoMode } from "react-icons/md";
import { CgUnavailable } from "react-icons/cg";

const columns = [
  { name: "ID", uid: "customer_id", sortable: true, style: { width: "200px" } },
  { name: "Name", uid: "customer_name", sortable: true },
  { name: "Status", uid: "status_automation", sortable: true },
  { name: "Actions", uid: "actions", style: { width: "50px" } },
];
const initial_visible_columns = [
  "customer_id",
  "customer_name",
  "status_automation",
  "actions",
];

const statusAutomationOptions = [
  { name: "Automatic", uid: "auto" },
  { name: "Review", uid: "pending_review" },
];

type CustomerType = {
  customer_id: string;
  customer_name: string;
  status_automation: string;
  customer_instruction: string;
};

export default function CustomerParams() {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();
  const axiosPrivate = useAxiosPrivate();
  const [filterValue, setFilterValue] = React.useState("");
  const [statusAutomationFilter, setStatusAutomationFilter] =
    React.useState<Selection>("all");

  const {
    data: apiResponse,
    isLoading,
    isError,
  } = useQuery<{ data: CustomerType[]; pagination: any }>({
    queryKey: ["CustomerSettings"],
    queryFn: async () => {
      const response = await axiosPrivate.get(`/customers/settings?per_page=1000`);
      return response.data;
    },
    staleTime: 6000000,
    gcTime: 6000000,
  });

  const customersSettingsData = React.useMemo(
    () => (apiResponse?.data ?? []).filter((item: any) => item.SK?.startsWith("SETTINGS#")),
    [apiResponse],
  );

  const filteredCustomers = React.useMemo(() => {
    let filtered = [...customersSettingsData];

    if (filterValue) {
      filtered = filtered.filter((customer) =>
        customer.customer_name
          .toLowerCase()
          .includes(filterValue.toLowerCase()),
      );
    }

    if (
      statusAutomationFilter !== "all" &&
      Array.from(statusAutomationFilter).length !==
        statusAutomationOptions.length
    ) {
      filtered = filtered.filter((customer) =>
        Array.from(statusAutomationFilter).includes(customer.status_automation),
      );
    }

    return filtered;
  }, [filterValue, statusAutomationFilter, customersSettingsData]);

  const classNames = React.useMemo(
    () => ({
      wrapper: ["max-h-[382px]"],
      table: ["border-separate", "border-spacing-y-2"],
      th: ["bg-transparent", "text-default-500", "border-b", "border-divider"],
      td: [
        "bg-white",
        "p-3",
        "group-data-[first=true]:first:before:rounded-none",
        "group-data-[first=true]:last:before:rounded-none",
        "group-data-[middle=true]:before:rounded-none",
        "group-data-[last=true]:first:before:rounded-none",
        "group-data-[last=true]:last:before:rounded-none",
      ],
      label: [
        "text-[10px] uppercase text-vistagrey/60 z-0 group-data-[filled=true]:text-vistagrey/60",
      ],
    }),
    [],
  );

  if (isLoading) {
    return <div>{t("Loading...")}</div>;
  }

  if (isError) {
    return <div>{t("Error loading data")}</div>;
  }

  const renderCell = (customer: CustomerType, columnKey: React.Key) => {
    const cellValue = customer[columnKey as keyof CustomerType];

    switch (columnKey) {
      case "status_automation":
        return (
          <Switch
            defaultSelected={customer.status_automation === "auto"}
            size="sm"
            color="primary"
            startContent={<MdOutlineAutoMode />}
            endContent={<CgUnavailable />}
            onChange={(e) =>
              handleStatusChange(customer.customer_id, e.target.checked)
            }
          >
            {t("mode_auto")}
          </Switch>
        );
      case "actions":
        return (
          <div className="relative flex justify-end items-center gap-2">
            <SecondaryActions customer_id={customer.customer_id} />
          </div>
        );
      default:
        return (
          <NavLink to={`/customers/settings/${customer.customer_id}`} className="flex items-center w-full h-full px-3 py-2">
            {cellValue}
          </NavLink>
        );
    }
  };

  const topContent = (
    <div className="flex gap-3">
      <Input
        isClearable
        classNames={{
          base: "w-full min-w-[75%]",
          inputWrapper: [
            "border-0",
            "bg-white",
            "h-9",
            "min-h-unit-9",
            "hover:bg-white",
            "group-data-[hover=true]:bg-white",
            "group-data-[focus=true]:bg-white",
          ],
          input: "text-vistagrey",
        }}
        placeholder={t("search")}
        size="md"
        startContent={<IoSearchSharp className="text-default-300" />}
        value={filterValue}
        onClear={() => setFilterValue("")}
        onValueChange={setFilterValue}
      />

      <Dropdown>
        <DropdownTrigger className="hidden sm:flex">
          <Button
            endContent={<LuChevronDown className="text-small" />}
            size="sm"
            variant="flat"
            radius="full"
            className="h-9 bg-white text-vistagrey text-small"
          >
            {t("status")}
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          disallowEmptySelection
          aria-label={"table_columns"}
          closeOnSelect={false}
          selectedKeys={statusAutomationFilter}
          selectionMode="multiple"
          onSelectionChange={setStatusAutomationFilter}
        >
          {statusAutomationOptions.map((status_automation) => (
            <DropdownItem
              key={status_automation.uid}
              textValue={status_automation.name || "N/A"}
              className="capitalize"
            >
              {t(status_automation.name)}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );

  const handleStatusChange = async (
    customer_id: string,
    newStatus: boolean,
  ) => {
    const data = { status_automation: newStatus ? "auto" : "pending_review" };
    try {
      await axiosPrivate.put(`/customers/settings/${customer_id}`, data);
      
      queryClient.setQueryData(["CustomerSettings"], (oldData: any) => {
        if (oldData) {
          return {
            ...oldData,
            data: oldData.data.map((customer: CustomerType) =>
              customer.customer_id === customer_id
                ? { ...customer, status_automation: data.status_automation }
                : customer
            ),
          };
        }
        return oldData;
      });

    } catch (error) {
      console.error("Failed to update status", error);
    }
  };

  return (
    <>
      <div className="flex w-full justify-end">
        <Button
          onClick={onOpen}
          className="bg-vistagreen text-white rounded-md p-3 absolute -mt-16"
        >
          {t("add_customer_settings")}
        </Button>
      </div>

      <DataTable<CustomerType>
        columns={columns}
        data={filteredCustomers || []}
        renderCell={renderCell}
        keyExtractor={(customer) => customer.customer_id}
        initialSortDescriptor={{
          column: "customer_name",
          direction: "ascending",
        }}
        initialVisibleColumns={initial_visible_columns}
        topContent={topContent}
        classNames={classNames}
      />
      <AddCustomerModal
        isOpen={isOpen}
        onClose={onClose}
        classNames={classNames}
      />
    </>
  );
}
